import { USER_ROLES } from '@didaskill/domain/users/constants';

export enum LESSON_VIEWS {
  MINIMAL = 'MINIMAL',
  LIST = 'LIST',
  INTERNAL = 'INTERNAL',
  FULL = 'FULL',
}

export enum LESSON_COMMENT_USER_VOTE {
  UP = 'UP',
  DOWN = 'DOWN',
}

export enum LESSON_EDITION_STATUSES {
  EDITING = 'EDITING',
  AWAITING_VALIDATION = 'AWAITING_VALIDATION',
}

export enum LESSON_EDITION_VERSION_TYPES {
  MAJOR = 'MAJOR',
  MINOR = 'MINOR',
}

export const LESSONS_SPECIAL_SORTS = {
  INTERNAL_PRIORITY: 'internalPriority',
  INTERNAL_PRIORITY_WITH_AWAITING_VALIDATION: 'internalPriorityWithAwaitingValidation',
} as const;

/** Sortable field for the number of ongoing (open / in progress) alerts on a lesson. */
export const LESSON_LIST_ONGOING_ALERTS_SORT_FIELD = 'ongoingAlertsCount';

/**
 * Ongoing alerts count buckets for the lessons list filter, in display order.
 */
export enum LESSON_ONGOING_ALERTS_COUNT_RANGES {
  ZERO = 'ZERO',
  ONE_TO_FIVE = 'ONE_TO_FIVE',
  SIX_TO_TEN = 'SIX_TO_TEN',
  ELEVEN_TO_NINETY_NINE = 'ELEVEN_TO_NINETY_NINE',
  ONE_HUNDRED_PLUS = 'ONE_HUNDRED_PLUS',
}

/** Inclusive bounds for each lessons-list ongoing-alerts filter bucket. */
export const LESSON_ONGOING_ALERTS_COUNT_RANGE_BOUNDS: Record<
  LESSON_ONGOING_ALERTS_COUNT_RANGES,
  { min: number; max?: number }
> = {
  [LESSON_ONGOING_ALERTS_COUNT_RANGES.ELEVEN_TO_NINETY_NINE]: { min: 11, max: 99 },
  [LESSON_ONGOING_ALERTS_COUNT_RANGES.ONE_HUNDRED_PLUS]: { min: 100 },
  [LESSON_ONGOING_ALERTS_COUNT_RANGES.ONE_TO_FIVE]: { min: 1, max: 5 },
  [LESSON_ONGOING_ALERTS_COUNT_RANGES.SIX_TO_TEN]: { min: 6, max: 10 },
  [LESSON_ONGOING_ALERTS_COUNT_RANGES.ZERO]: { min: 0, max: 0 },
};

/** Sortable field for the number of skills linked to a lesson. */
export const LESSON_LIST_SKILLS_COUNT_SORT_FIELD = 'skillsCount';

/** Sortable field for the number of sequence likes on a lesson. */
export const LESSON_LIST_LIKES_COUNT_SORT_FIELD = 'likesCount';

/** Sortable field for the number of sequence dislikes on a lesson. */
export const LESSON_LIST_DISLIKES_COUNT_SORT_FIELD = 'dislikesCount';

/**
 * Edition statuses used to filter the lessons list.
 * Display order in the backoffice filter is {@link LESSON_LIST_STATUS_FILTER_ORDER}.
 */
export enum LESSON_LIST_STATUSES {
  AWAITING_VALIDATION = 'AWAITING_VALIDATION',
  EDITING = 'EDITING',
  PUBLISHED = 'PUBLISHED',
}

/** Display order of lesson list status filter options. */
export const LESSON_LIST_STATUS_FILTER_ORDER: LESSON_LIST_STATUSES[] = [
  LESSON_LIST_STATUSES.PUBLISHED,
  LESSON_LIST_STATUSES.EDITING,
  LESSON_LIST_STATUSES.AWAITING_VALIDATION,
];

export const EMPTY_SEQUENCE_CONTENT = '<h3 id="h3-1">Titre de la séquence</h3>';

export enum LESSON_SEQUENCE_TYPES {
  TEXT = 'TEXT',
  TRAINING = 'TRAINING',
  ACTIVITY = 'ACTIVITY',
}

export enum ACTIVITY_TYPES {
  SCORM = 'SCORM',
}

export enum LESSON_EXERCISE_STYLES {
  MCQ = 'MCQ',
}

// Editable sequence lock duration (in minutes).
export const EDITABLE_SEQUENCE_LOCK_TIMEOUT = 5;

export const LESSON_CARD_THUMBNAIL_ASPECT_RATIO = 235 / 120;

export const LESSON_OUTLINE_MIN_LEVEL = 3;
export const LESSON_OUTLINE_MAX_LEVEL = 5;

export enum BLOCK_STATUSES {
  CREATED = 'CREATED',
  MODIFIED = 'MODIFIED',
  DELETED = 'DELETED',
}

export enum CHANGELOG_STATUSES {
  CREATED = 'CREATED',
  MODIFIED = 'MODIFIED',
  DELETED = 'DELETED',
}

export enum LESSON_USER_ROLES {
  MANAGER = 'MANAGER',
  TEACHER = 'TEACHER',
}

export const LESSON_RESPONSIBLE_ELIGIBLE_ROLES: USER_ROLES[] = [
  USER_ROLES.ADMIN,
  USER_ROLES.CONCEPTION,
  USER_ROLES.EDITOR,
  USER_ROLES.EXTERNAL_EDITOR,
  USER_ROLES.TEACHER,
];

export const LESSON_EDITABLE_VERSION_EDITOR_ELIGIBLE_ROLES: USER_ROLES[] = [
  USER_ROLES.EDITOR,
  USER_ROLES.EXTERNAL_EDITOR,
];

/** PDF generation lifecycle for a published lesson version (backoffice / worker). */
export enum LESSON_VERSION_PDF_GENERATION_STATES {
  IDLE = 'idle',
  PENDING = 'pending',
}

export const LESSON_RESOURCE_TITLE_MAX_LENGTH = 150;

export const LESSON_RESOURCE_DESCRIPTION_MAX_LENGTH = 500;

export const LESSON_RESOURCE_PICTURE_ASPECT_RATIO = 235 / 120;

export const NB_DAYS_BEFORE_SEQUENCE_DOWNLOAD_ENABLED = 30;

export * from './error-codes';
export * from './model';
